import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApiResponse } from "../../../api/apiResponse";
import { showStudentSchedule } from "../../../api/endpoints/students/schedules";
import {
  indexStudentSessions,
  showInProgressSessions,
} from "../../../api/endpoints/students/sessions";
import {
  getStudent,
  showStudentStatistics,
} from "../../../api/endpoints/students/students";
import ScheduleModel from "../../../api/models/ScheduleModel";
import SessionModel from "../../../api/models/SessionModel";
import StudentModel from "../../../api/models/StudentModel";
import BarChart from "../../../components/charts/BarCharts";
import LineChart from "../../../components/charts/LineCharts";
import {
  getLineChartOptions,
  getRadialBarPagesProgressOptions,
  getPieChartSessionStatusOptions,
  getBarChartOptions,
} from "../../../components/charts/Options";
import PieChart from "../../../components/charts/PieCharts";
import {
  openSessionInNewTab,
  sortByDateAndStartTime,
} from "../../../components/sessions/helper";
import SessionCardHome from "../../../components/sessions/SessionCardHome";
import { useAuth } from "../../../context/AuthContext";
import { makeApiRequest, showApiErrorMessages } from "../../../helpers";
import { StudentScheduleService } from "../../../services/studentSessionsService";
import { StatisticsType } from "../../../api/@types/scheduleTypes";
import LearnDashUserModel from "../../../api/models/LearnDashUserModel";
import { useQuery } from "react-query";
import CandidateModel from "../../../api/models/CandidateModel";
import LevelProgressHomeStudent from "../../../components/sessions/LevelProgressHomeStudent";
import CandidateCourseModel from "../../../api/models/CandidateCourseModel";
import LoadingSpinner from "../../../components/LoadingSpinner";
const LEARN_DASH_URL = process.env.REACT_APP_LEARN_DASH_BASE_URL;

export default function RoomPage() {
  const { currentUser } = useAuth()!;
  const student = currentUser as StudentModel;

  const LocalBasePath = "page.student.dashboard.room";
  const { t } = useTranslation();
  const LocalBaseCharts = "charts";
  const [inProgressSession, setInProgressSession] = useState<any>(null);
  const [sessionNumber, setSessionNumber] = useState<any>(1);
  const [sessions, setSessions] = useState<SessionModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [schedule, setSchedule] = useState<ScheduleModel | undefined>();
  const [displayedSessions, setDisplayedSessions] = useState<SessionModel[]>(
    []
  );
  const [sessionsNotes, setSessionsNotes] = useState<{
    passedNotes: number[];
    unpassedNotes: number[];
    sessionIds: string[];
  }>({ passedNotes: [], unpassedNotes: [], sessionIds: [] });
  const [quranPagesProgress, setQuranPagesProgress] = useState<{
    finished: number;
    remaining: number;
  }>({ finished: 0, remaining: 644 });
  const [courses, setCourses] = useState<CandidateCourseModel[]>([]);
  const [sessionTardiness, setSessionTardiness] = useState([]);
  const [sessionsStatistics, setSessionsStatistics] = useState<{
    total_sessions_count: number;
    finished_sessions_count: number;
    cancelled_sessions_count: number;
  }>({
    total_sessions_count: 0,
    finished_sessions_count: 0,
    cancelled_sessions_count: 0,
  });
  const navigate = useNavigate();
  const LabelsSessionStatus = [
    t(`${LocalBaseCharts}.scheduled`),
    t(`${LocalBaseCharts}.done`),
    t(`${LocalBaseCharts}.canceled`),
  ];
  const LabelsPagesProgress = [
    t(`${LocalBaseCharts}.remaining`),
    t(`${LocalBaseCharts}.left`),
  ];
  const SessionStatusPieData = [
    sessionsStatistics.total_sessions_count || 0,
    sessionsStatistics.finished_sessions_count || 0,
    sessionsStatistics.cancelled_sessions_count || 0,
  ];
  const PagesProgressPieData = [
    quranPagesProgress.remaining,
    quranPagesProgress.finished,
  ];
  const { data: learnDashUser } = useQuery("learnDashUser", getStudent, {
    staleTime: Infinity,
    select: (response) => {
      let learnDashUser: LearnDashUserModel | null = null;
      if (response.isSuccess) {
        const candidate: CandidateModel = CandidateModel.fromApiResponse(
          response.data
        );
        learnDashUser = candidate.learnDashUser;
      } else {
        showApiErrorMessages(response);
      }
      return learnDashUser;
    },
  });
  const getInProgressSessions = async () => {
    const response: ApiResponse = await showInProgressSessions();
    if (response.isSuccess && response.statusCode !== 204) {
      setInProgressSession(response.data);
    } else {
      setInProgressSession(null);
    }
  };

  useEffect(() => {
    setInterval(() => {
      getInProgressSessions();
    }, 1000);
  }, []);

  const getStatistics = useCallback(async () => {
    try {
      const res = await showStudentStatistics();

      if (res.isSuccess) {
        const { learn_dash_progresses }: StatisticsType = res.data;
        const {
          notes_statistics,
          quran_pages_statistics,
          session_status_statistics,
          session_tardiness,
        }: StatisticsType = res.data.statistics;
        const ListCourses = CandidateCourseModel.fromApiResponse(
          learn_dash_progresses
        );
        setCourses(ListCourses);
        setSessionTardiness(session_tardiness);
        setQuranPagesProgress({
          finished: quran_pages_statistics.total_read_pages,
          remaining:
            quran_pages_statistics.total_pages -
            quran_pages_statistics.total_read_pages,
        });
        setSessionsStatistics({
          total_sessions_count: session_status_statistics?.scheduled,
          finished_sessions_count: session_status_statistics?.completed,
          cancelled_sessions_count: session_status_statistics?.canceled,
        });
        setSessionsNotes({
          passedNotes: notes_statistics.passed_notes_counts,
          unpassedNotes: notes_statistics.failed_notes_counts,
          sessionIds: [...Array(notes_statistics.total_sessions_count)].map(
            (_, i) => `#${i + 1}`
          ),
        });
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  }, [student]);

  useEffect(() => {
    getStatistics();
  }, []);

  // API calls
  const fetchSessions = async () => {
    let userSessions: SessionModel[] = [];
    await makeApiRequest(
      () => indexStudentSessions({ orderBy: "session_number" }),
      (res: ApiResponse) => {
        userSessions = SessionModel.fromApiResponse(res.data);
      }
    );
    const sortedSessions = userSessions;
    setSessions(sortedSessions);
  };

  const fetchSchedule = async () => {
    let fetchedSchedule: ScheduleModel | undefined;
    await makeApiRequest(showStudentSchedule, (res: ApiResponse) => {
      fetchedSchedule = ScheduleModel.fromApiResponse(res.data);
    });
    setSchedule(fetchedSchedule);
  };

  const setLastCompletedSessionNumber = () => {
    if (!!sessions && sessions.length > 0) {
      const lastSession = sessions.at(-1);
      setSessionNumber(lastSession?.session_number);
    }
  };

  // Fetch schedule and sessions on mount
  useEffect(() => {
    fetchSchedule();
    fetchSessions();
  }, []);

  // Set the last session number once sessions are fetched
  useEffect(() => {
    setLastCompletedSessionNumber();
  }, [sessions]);

  // Set displayed sessions
  useEffect(() => {
    if (!!schedule && !!sessions) {
      const studentSessionsService = new StudentScheduleService(
        sessions,
        schedule
      );
      const activeSessions = studentSessionsService.generateActiveSessions();
      setDisplayedSessions(activeSessions);
    }
  }, [sessions, schedule]);

  return (
    <div className="w-full min-h-[90vh] flex flex-col  bg-gray-50">
      <div className="px-6 -mx-4 md:px-0 ring-1 ring-gray-300 sm:-mx-6 md:mx-0">
        <div className="px-6 py-4 text-gray-900 divide-gray-300 sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <h2 className="text-2xl ">{t(`${LocalBasePath}.readingRoom`)}</h2>
          {!!inProgressSession && (
            <button
              onClick={() =>
                openSessionInNewTab("student")(inProgressSession.id)
              }
              data-cy="entrySession"
              type="button"
              className="inline-flex items-center px-4 py-2 text-base font-medium border border-gray-300 rounded-md shadow-sm text-gray-50 bg-brandblue-500 hover:bg-brandblue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              {t(`${LocalBasePath}.entryToTheSession`)}
              <ArrowLeftIcon
                className="w-5 h-5 ml-3 -mr-1 rtl:mr-3 rtl:-ml-1 ltr:rotate-180"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
      <div className="p-8 px-6">
        <div className="w-full">
          {displayedSessions.length > 0 && (
            <>
              <div className="w-full">
                <h1 className="text-lg font-medium leading-6 text-slate-900">
                  {t(`${LocalBasePath}.sessionInfo`)}
                </h1>
              </div>
              <ul
                className="grid grid-cols-1 gap-0 my-5 md:grid-cols-3 md:gap-4"
                data-cy="activeSessionsList"
              >
                {displayedSessions.map((session: any, index: number) => (
                  <div
                    key={index}
                    className="w-full my-4 overflow-hidden bg-white rounded-lg shadow md:my-0"
                  >
                    <SessionCardHome
                      sessionNumber={sessionNumber + index}
                      session={session}
                    />
                  </div>
                ))}
              </ul>
            </>
          )}
          <div className="w-full mt-8 md:mt-12">
            <h2 className="text-lg font-semibold text-gray-900">
              {t(`${LocalBasePath}.statistics`)}
            </h2>
          </div>
          {isLoading && (
            <div className="min-h-[80vh] w-full flex justify-center items-center">
              <LoadingSpinner size={100} />
            </div>
          )}
          {!isLoading && (
            <div>
              <div className="w-full bg-white rounded-lg shadow py-1.5 px-2 pb-4  md:px-6  my-5">
                <div className="w-full mb-5">
                  <div className="flex items-center justify-between w-full">
                    <h3 className="text-xs font-semibold leading-5 text-gray-900">
                      {t(`${LocalBasePath}.readingProgress`)}
                    </h3>
                    <form
                      action={`${LEARN_DASH_URL}/wp-login.php`}
                      method="POST"
                      target="_blank"
                    >
                      <input
                        type="hidden"
                        name="log"
                        value={learnDashUser?.email}
                      />
                      <input
                        type="hidden"
                        name="pwd"
                        value={learnDashUser?.password}
                      />
                      <input
                        type="hidden"
                        name="redirect_to"
                        value={`${LEARN_DASH_URL}/ar/ld-groups/${learnDashUser?.slug}/`}
                      />
                      <button
                        type="submit"
                        className=" inline-flex items-center  border-none  bg-transparent  px-4 py-2.5 text-xs font-semibold leading-5 text-brandblue-600   focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0"
                      >
                        <span className="flex">
                          {" "}
                          {t(`${LocalBasePath}.enterLearnDash`)}
                        </span>
                      </button>
                    </form>
                  </div>
                </div>
                <LevelProgressHomeStudent listOfLevels={courses} />
              </div>
              <div className="grid grid-cols-1 gap-0 md:grid-cols-3 md:gap-4">
                <div className="w-full my-4 overflow-hidden bg-white rounded-lg shadow md:my-0">
                  <div className="px-4 py-5 sm:p-6">
                    <h1> {t(`${LocalBasePath}.sessionStatus`)}</h1>
                    <div className="w-full h-full">
                      <PieChart
                        options={getPieChartSessionStatusOptions(
                          LabelsSessionStatus,
                          SessionStatusPieData
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full col-span-2 overflow-hidden bg-white rounded-lg shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <h1>
                      {" "}
                      {t(`${LocalBasePath}.observedNotesForEachSession`)}
                    </h1>
                    <div>
                      <BarChart
                        options={getBarChartOptions(
                          [
                            {
                              name: t(`${LocalBaseCharts}.passed`),
                              data: sessionsNotes.passedNotes,
                            },
                            {
                              name: t(`${LocalBaseCharts}.unpassed`),
                              data: sessionsNotes.unpassedNotes,
                            },
                          ],
                          sessionsNotes.sessionIds
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-0 pt-4 md:grid-cols-3 md:gap-4">
                <div className="w-full col-span-2 my-4 overflow-hidden bg-white rounded-lg shadow md:my-0">
                  <div className="px-4 py-5 sm:p-6">
                    <h1> {t(`${LocalBasePath}.lateTimeForSessions`)}</h1>
                    <div>
                      <LineChart
                        options={getLineChartOptions(
                          t("charts.duration"),
                          sessionTardiness
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full overflow-hidden bg-white rounded-lg shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <h1> {t(`${LocalBasePath}.progressPages`)}</h1>
                    <div>
                      <PieChart
                        options={getRadialBarPagesProgressOptions(
                          LabelsPagesProgress,
                          PagesProgressPieData
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
